// chain29 Restaurant OS tokens (docs/design_refs/restaurant_os/System.dc.html).
// Orange is reserved for the one action that moves the order forward on each
// screen; money-moving actions on the terminal use dark ink.

export const C = {
  orange: '#F97316',
  orangeInk: '#C2410C',
  orangeDeep: '#1A0D02',
  orangeSoft: '#FFF1E6',
  orangeTint: '#FFF7F0',
  floor: '#0B0E13',
  bar: '#0F1319',
  panel: '#121820',
  panel2: '#161C24',
  tile: '#0F141B',
  line: '#1E252E',
  line2: '#2A323D',
  textHi: '#F8FAFC',
  text: '#E6EAF0',
  textMid: '#A7B0BC',
  textLo: '#8A94A3',
  textDim: '#7D8796',
  textOff: '#5D6775',
  paper: '#F6F3EF',
  card: '#FFFFFF',
  ink: '#1C1917',
  ink2: '#44403C',
  ink3: '#57534E',
  muted: '#78716C',
  faint: '#A8A29E',
  border: '#E7E0D8',
  hair: '#F0EAE3',
  hair2: '#F4EFE9',
  sand: '#F5F2EE',
  cream: '#FCFAF8',
  green: '#16A34A',
  greenHi: '#22C55E',
  greenText: '#4ADE80',
  greenInk: '#15803D',
  greenSoft: '#DCFCE7',
  red: '#DC2626',
  redHi: '#EF4444',
  redInk: '#B91C1C',
  redSoft: '#FEE2E2',
  amber: '#F59E0B',
  amberHi: '#FBBF24',
  amberInk: '#92400E',
  amberSoft: '#FEF3C7',
  blue: '#2563EB',
  blueHi: '#3B82F6',
  blueInk: '#1D4ED8',
  blueSoft: '#DBEAFE',
  violet: '#6D28D9',
  violetHi: '#8B5CF6',
  violetSoft: '#EDE9FE',
  slate: '#475569',
};

export const FONT_TIGHT = "'Inter Tight', 'Inter', system-ui, sans-serif";

// Table states on the dark floor (Main.dc.html). Keys = the API's derived floor status.
export const TABLE_STATE = {
  available: { label: 'Available', fill: 'rgba(34,197,94,0.10)', ring: '#22C55E', text: '#4ADE80', chair: '#1F3B2B', chipBg: 'rgba(34,197,94,.14)', chipFg: '#4ADE80' },
  seated: { label: 'Seated', fill: '#1E3A5F', ring: '#3B82F6', text: '#FFFFFF', chair: '#28457F', chipBg: 'rgba(59,130,246,.18)', chipFg: '#93C5FD' },
  ordering: { label: 'Ordering', fill: '#1E40AF', ring: '#3B82F6', text: '#FFFFFF', chair: '#28457F', chipBg: 'rgba(59,130,246,.18)', chipFg: '#93C5FD' },
  dining: { label: 'Dining', fill: '#334155', ring: '#64748B', text: '#F1F5F9', chair: '#3B4757', chipBg: 'rgba(100,116,139,.25)', chipFg: '#CBD5E1' },
  food_ready: { label: 'Food ready', fill: '#F59E0B', ring: '#FBBF24', text: '#1F1300', chair: '#7A4E0C', chipBg: 'rgba(245,158,11,.18)', chipFg: '#FBBF24' },
  checkout_requested: { label: 'Check requested', fill: '#6D28D9', ring: '#8B5CF6', text: '#FFFFFF', chair: '#4C2C91', chipBg: 'rgba(139,92,246,.2)', chipFg: '#C4B5FD' },
  needs_attention: { label: 'Needs attention', fill: '#B91C1C', ring: '#EF4444', text: '#FFFFFF', chair: '#7F2222', chipBg: 'rgba(239,68,68,.18)', chipFg: '#FCA5A5' },
};
export const TABLE_STATE_ORDER = ['available', 'seated', 'ordering', 'dining', 'food_ready', 'checkout_requested', 'needs_attention'];

// Kitchen line states as chips (light surfaces).
export const LINE_CHIP = {
  draft: { label: 'Not sent', bg: '#FFF1E6', fg: '#C2410C', dot: '#F97316' },
  new: { label: 'Sent', bg: '#F5F2EE', fg: '#57534E', dot: '#A8A29E' },
  preparing: { label: 'Preparing', bg: '#DBEAFE', fg: '#1D4ED8', dot: '#3B82F6' },
  ready: { label: 'Ready', bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  served: { label: 'Served', bg: '#F1F5F9', fg: '#475569', dot: '#64748B' },
  voided: { label: 'Voided', bg: '#FEE2E2', fg: '#B91C1C', dot: '#EF4444' },
  sent: { label: 'Sent', bg: '#F5F2EE', fg: '#57534E', dot: '#A8A29E' },
};

// The same chips on dark surfaces (floor side sheet).
export const LINE_CHIP_DARK = {
  draft: { label: 'Not sent', bg: 'rgba(249,115,22,.16)', fg: '#FDBA74' },
  new: { label: 'Sent', bg: 'rgba(148,163,184,.16)', fg: '#CBD5E1' },
  preparing: { label: 'Preparing', bg: 'rgba(59,130,246,.2)', fg: '#93C5FD' },
  ready: { label: 'Ready', bg: 'rgba(245,158,11,.2)', fg: '#FBBF24' },
  served: { label: 'Served', bg: 'rgba(100,116,139,.25)', fg: '#CBD5E1' },
  cancelled: { label: 'Cancelled', bg: 'rgba(239,68,68,.2)', fg: '#FCA5A5' },
  late: { label: 'Late', bg: 'rgba(239,68,68,.2)', fg: '#FCA5A5' },
};

// KDS age band: green under 5 min, orange 5-10, red over 10.
export function ageBand(seconds) {
  const m = (seconds || 0) / 60;
  if (m >= 10) return '#DC2626';
  if (m >= 5) return '#EA580C';
  return '#16A34A';
}

export const COURSES = [
  { value: '1', label: 'Starters' },
  { value: '2', label: 'Mains' },
  { value: '3', label: 'Dessert' },
];

export function courseLabel(course) {
  if (!course) return null;
  const known = COURSES.find((c) => c.value === String(course));
  return known ? `Course ${known.value} · ${known.label}` : `Course ${course}`;
}
